'use client';

/**
 * Risk Register Page
 * Track and manage technical and operational risks
 */

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { WorkspaceProvider, useWorkspace } from '@/lib/archimate/WorkspaceContext';
import { CommandCenterProvider, useCommandCenter } from '@/lib/command-center/CommandCenterContext';
import CommandCenterHeader from '@/components/command-center/CommandCenterHeader';

interface Risk {
  id: string;
  workspace_id: string;
  title: string;
  description?: string;
  category: 'technical' | 'operational' | 'security' | 'compliance' | 'vendor' | 'capacity' | 'skill' | 'financial' | 'other';
  likelihood: 'rare' | 'unlikely' | 'possible' | 'likely' | 'almost_certain';
  impact: 'negligible' | 'minor' | 'moderate' | 'major' | 'catastrophic';
  risk_score?: number;
  status: 'identified' | 'assessing' | 'mitigating' | 'accepted' | 'resolved' | 'closed';
  response_strategy?: 'avoid' | 'mitigate' | 'transfer' | 'accept';
  mitigation_plan?: string;
  contingency_plan?: string;
  review_date?: string;
  ticket_url?: string;
  owner_name?: string;
  tags?: string[];
  created_at: string;
}

interface RiskStats {
  total: number;
  critical: number;
  high: number;
  mitigating: number;
}

const CATEGORY_CONFIG: Record<string, { label: string; icon: string }> = {
  technical: { label: 'Technical', icon: '💻' },
  operational: { label: 'Operational', icon: '⚙️' },
  security: { label: 'Security', icon: '🔒' },
  compliance: { label: 'Compliance', icon: '📋' },
  vendor: { label: 'Vendor', icon: '🏢' },
  capacity: { label: 'Capacity', icon: '📊' },
  skill: { label: 'Skill Gap', icon: '👥' },
  financial: { label: 'Financial', icon: '💰' },
  other: { label: 'Other', icon: '📦' },
};

const LIKELIHOOD_CONFIG = {
  rare: { label: 'Rare', value: 1 },
  unlikely: { label: 'Unlikely', value: 2 },
  possible: { label: 'Possible', value: 3 },
  likely: { label: 'Likely', value: 4 },
  almost_certain: { label: 'Almost Certain', value: 5 },
};

const IMPACT_CONFIG = {
  negligible: { label: 'Negligible', value: 1 },
  minor: { label: 'Minor', value: 2 },
  moderate: { label: 'Moderate', value: 3 },
  major: { label: 'Major', value: 4 },
  catastrophic: { label: 'Catastrophic', value: 5 },
};

const STATUS_CONFIG = {
  identified: { label: 'Identified', color: 'bg-gray-500 text-white' },
  assessing: { label: 'Assessing', color: 'bg-blue-500 text-white' },
  mitigating: { label: 'Mitigating', color: 'bg-yellow-500 text-black' },
  accepted: { label: 'Accepted', color: 'bg-purple-500 text-white' },
  resolved: { label: 'Resolved', color: 'bg-green-500 text-white' },
  closed: { label: 'Closed', color: 'bg-gray-400 text-white' },
};

const RESPONSE_CONFIG = {
  avoid: { label: 'Avoid', description: 'Eliminate the risk entirely' },
  mitigate: { label: 'Mitigate', description: 'Reduce likelihood or impact' },
  transfer: { label: 'Transfer', description: 'Shift risk to third party' },
  accept: { label: 'Accept', description: 'Acknowledge and monitor' },
};

function getRiskLevel(score: number): { label: string; color: string; bgColor: string } {
  if (score >= 20) return { label: 'Critical', color: 'text-red-400', bgColor: 'bg-red-500/20' };
  if (score >= 12) return { label: 'High', color: 'text-accent-400', bgColor: 'bg-accent-500/20' };
  if (score >= 6) return { label: 'Medium', color: 'text-yellow-400', bgColor: 'bg-yellow-500/20' };
  return { label: 'Low', color: 'text-green-400', bgColor: 'bg-green-500/20' };
}

function RisksContent() {
  const { currentWorkspace, loading: workspaceLoading } = useWorkspace();
  const { setWorkspaceId } = useCommandCenter();

  const [risks, setRisks] = useState<Risk[]>([]);
  const [stats, setStats] = useState<RiskStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Form state
  const [newRisk, setNewRisk] = useState<{
    title: string;
    description: string;
    category: Risk['category'];
    likelihood: Risk['likelihood'];
    impact: Risk['impact'];
    response_strategy: string;
    mitigation_plan: string;
    owner_name: string;
    ticket_url: string;
  }>({
    title: '',
    description: '',
    category: 'technical',
    likelihood: 'possible',
    impact: 'moderate',
    response_strategy: '',
    mitigation_plan: '',
    owner_name: '',
    ticket_url: '',
  });

  useEffect(() => {
    if (currentWorkspace?.id) {
      setWorkspaceId(currentWorkspace.id);
    }
  }, [currentWorkspace?.id, setWorkspaceId]);

  const loadRisks = useCallback(async () => {
    if (!currentWorkspace?.id) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ workspace_id: currentWorkspace.id });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);

      const response = await fetch(`/api/secure/command-center/risks?${params}`);
      if (!response.ok) throw new Error('Failed to load risks');

      const data = await response.json();
      setRisks(data.risks || []);
      setStats(data.stats || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load risks');
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id, statusFilter, categoryFilter]);

  useEffect(() => {
    loadRisks();
  }, [loadRisks]);

  const handleCreateRisk = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentWorkspace?.id) return;

    try {
      const response = await fetch('/api/secure/command-center/risks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: currentWorkspace.id,
          title: newRisk.title,
          description: newRisk.description || undefined,
          category: newRisk.category,
          likelihood: newRisk.likelihood,
          impact: newRisk.impact,
          response_strategy: newRisk.response_strategy || undefined,
          mitigation_plan: newRisk.mitigation_plan || undefined,
          owner_name: newRisk.owner_name || undefined,
          ticket_url: newRisk.ticket_url || undefined,
        }),
      });

      if (!response.ok) throw new Error('Failed to create risk');

      setShowCreateModal(false);
      setNewRisk({
        title: '',
        description: '',
        category: 'technical',
        likelihood: 'possible',
        impact: 'moderate',
        response_strategy: '',
        mitigation_plan: '',
        owner_name: '',
        ticket_url: '',
      });
      loadRisks();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create risk');
    }
  };

  const calculatedScore = LIKELIHOOD_CONFIG[newRisk.likelihood].value * IMPACT_CONFIG[newRisk.impact].value;

  if (workspaceLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-foreground-tertiary text-sm mb-2">
          <Link href="/command-center" className="hover:text-accent">Command Center</Link>
          <span>/</span>
          <span>Risks</span>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Risk Register</h1>
            <p className="text-foreground-tertiary mt-1">Track and manage technical and operational risks</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Risk
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4 text-red-200 mb-6">
            {error}
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-surface/30 border border-border-muted rounded-lg p-4">
              <p className="text-foreground-tertiary text-sm mb-1">Total Risks</p>
              <span className="text-4xl font-bold text-foreground">{stats.total}</span>
            </div>
            <div className="bg-surface/30 border border-red-500/30 rounded-lg p-4">
              <p className="text-foreground-tertiary text-sm mb-1">Critical</p>
              <span className="text-4xl font-bold text-red-400">{stats.critical}</span>
            </div>
            <div className="bg-surface/30 border border-accent-500/30 rounded-lg p-4">
              <p className="text-foreground-tertiary text-sm mb-1">High</p>
              <span className="text-4xl font-bold text-accent-400">{stats.high}</span>
            </div>
            <div className="bg-surface/30 border border-yellow-500/30 rounded-lg p-4">
              <p className="text-foreground-tertiary text-sm mb-1">Being Mitigated</p>
              <span className="text-4xl font-bold text-yellow-400">{stats.mitigating}</span>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div>
            <label className="text-xs text-foreground-tertiary mb-1 block">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-surface border border-border rounded-lg px-3 py-1.5 text-foreground text-sm focus:outline-none focus:border-accent"
            >
              <option value="all">All Statuses</option>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-foreground-tertiary mb-1 block">Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="bg-surface border border-border rounded-lg px-3 py-1.5 text-foreground text-sm focus:outline-none focus:border-accent"
            >
              <option value="all">All Categories</option>
              {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>{config.icon} {config.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
          </div>
        )}

        {/* Risks List */}
        {!loading && (
          <div className="space-y-4">
            {risks.length === 0 ? (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cream/5 mb-4">
                  <span className="text-3xl">✅</span>
                </div>
                <p className="text-foreground-tertiary text-lg">No risks found</p>
                <p className="text-foreground-muted mt-2">Start tracking technical and operational risks</p>
              </div>
            ) : (
              risks.map((risk) => {
                const categoryConfig = CATEGORY_CONFIG[risk.category];
                const statusConfig = STATUS_CONFIG[risk.status];
                const score = risk.risk_score || (LIKELIHOOD_CONFIG[risk.likelihood].value * IMPACT_CONFIG[risk.impact].value);
                const riskLevel = getRiskLevel(score);

                return (
                  <div
                    key={risk.id}
                    className={`bg-surface/30 border border-border-muted rounded-lg p-4 ${riskLevel.bgColor}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">{categoryConfig.icon}</span>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusConfig.color}`}>
                              {statusConfig.label}
                            </span>
                            <span className={`text-xs font-medium ${riskLevel.color}`}>
                              Risk Score: {score} ({riskLevel.label})
                            </span>
                          </div>
                          <h3 className="text-foreground font-semibold">{risk.title}</h3>
                          {risk.description && (
                            <p className="text-foreground-tertiary text-sm mt-1">{risk.description}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Risk Matrix Display */}
                    <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-background/50 rounded-lg">
                      <div>
                        <p className="text-foreground-tertiary text-xs mb-1">Likelihood</p>
                        <p className="text-foreground font-medium">{LIKELIHOOD_CONFIG[risk.likelihood].label}</p>
                      </div>
                      <div>
                        <p className="text-foreground-tertiary text-xs mb-1">Impact</p>
                        <p className="text-foreground font-medium">{IMPACT_CONFIG[risk.impact].label}</p>
                      </div>
                    </div>

                    {/* Mitigation Plan */}
                    {risk.mitigation_plan && (
                      <div className="mb-4 p-3 bg-background/50 rounded-lg">
                        <p className="text-foreground-tertiary text-xs mb-1">Mitigation Plan</p>
                        <p className="text-foreground-secondary text-sm">{risk.mitigation_plan}</p>
                      </div>
                    )}

                    {/* Meta */}
                    <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-tertiary">
                      {risk.response_strategy && (
                        <span>Strategy: {RESPONSE_CONFIG[risk.response_strategy].label}</span>
                      )}
                      {risk.owner_name && (
                        <div className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span>{risk.owner_name}</span>
                        </div>
                      )}
                      {risk.review_date && (
                        <span>Review: {new Date(risk.review_date).toLocaleDateString()}</span>
                      )}
                      {risk.ticket_url && (
                        <a
                          href={risk.ticket_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent hover:underline"
                        >
                          View Ticket
                        </a>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-background border border-border rounded-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold text-foreground mb-4">Add Risk</h2>
              <form onSubmit={handleCreateRisk}>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-foreground-secondary mb-1 block">Title *</label>
                    <input
                      type="text"
                      required
                      value={newRisk.title}
                      onChange={(e) => setNewRisk({ ...newRisk, title: e.target.value })}
                      className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-accent"
                      placeholder="Brief description of the risk"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-foreground-secondary mb-1 block">Description</label>
                    <textarea
                      value={newRisk.description}
                      onChange={(e) => setNewRisk({ ...newRisk, description: e.target.value })}
                      className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-accent h-20"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-foreground-secondary mb-1 block">Category *</label>
                    <select
                      value={newRisk.category}
                      onChange={(e) => setNewRisk({ ...newRisk, category: e.target.value as Risk['category'] })}
                      className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-accent"
                    >
                      {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                        <option key={key} value={key}>{config.icon} {config.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-foreground-secondary mb-1 block">Likelihood *</label>
                      <select
                        value={newRisk.likelihood}
                        onChange={(e) => setNewRisk({ ...newRisk, likelihood: e.target.value as Risk['likelihood'] })}
                        className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-accent"
                      >
                        {Object.entries(LIKELIHOOD_CONFIG).map(([key, config]) => (
                          <option key={key} value={key}>{config.label} ({config.value})</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm text-foreground-secondary mb-1 block">Impact *</label>
                      <select
                        value={newRisk.impact}
                        onChange={(e) => setNewRisk({ ...newRisk, impact: e.target.value as Risk['impact'] })}
                        className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-accent"
                      >
                        {Object.entries(IMPACT_CONFIG).map(([key, config]) => (
                          <option key={key} value={key}>{config.label} ({config.value})</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Calculated Risk Score */}
                  <div className={`p-3 rounded-lg ${getRiskLevel(calculatedScore).bgColor}`}>
                    <p className="text-foreground-secondary text-sm">
                      Calculated Risk Score: <span className={`font-bold ${getRiskLevel(calculatedScore).color}`}>
                        {calculatedScore} ({getRiskLevel(calculatedScore).label})
                      </span>
                    </p>
                  </div>

                  <div>
                    <label className="text-sm text-foreground-secondary mb-1 block">Response Strategy</label>
                    <select
                      value={newRisk.response_strategy}
                      onChange={(e) => setNewRisk({ ...newRisk, response_strategy: e.target.value })}
                      className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-accent"
                    >
                      <option value="">Select strategy...</option>
                      {Object.entries(RESPONSE_CONFIG).map(([key, config]) => (
                        <option key={key} value={key}>{config.label} - {config.description}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-foreground-secondary mb-1 block">Mitigation Plan</label>
                    <textarea
                      value={newRisk.mitigation_plan}
                      onChange={(e) => setNewRisk({ ...newRisk, mitigation_plan: e.target.value })}
                      className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-accent h-20"
                      placeholder="How will this risk be mitigated?"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-foreground-secondary mb-1 block">Owner</label>
                    <input
                      type="text"
                      value={newRisk.owner_name}
                      onChange={(e) => setNewRisk({ ...newRisk, owner_name: e.target.value })}
                      className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-accent"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-foreground-secondary mb-1 block">Ticket URL</label>
                    <input
                      type="url"
                      value={newRisk.ticket_url}
                      onChange={(e) => setNewRisk({ ...newRisk, ticket_url: e.target.value })}
                      className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-accent"
                      placeholder="https://..."
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-foreground-secondary hover:text-foreground"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover"
                  >
                    Add Risk
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function RisksPage() {
  return (
    <WorkspaceProvider>
      <CommandCenterProvider>
        <CommandCenterHeader />
        <RisksContent />
      </CommandCenterProvider>
    </WorkspaceProvider>
  );
}
